import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LocationProvider } from "@/contexts/LocationContext";
import AITriage from "@/pages/AITriage";
import PartnerPortal from "@/pages/PartnerPortal";
import DriverApp from "@/pages/DriverApp";
import FieldExec from "@/pages/FieldExec";
import AdminHospitals from "@/pages/AdminHospitals";
import AdminLabs from "@/pages/AdminLabs";
import AdminPharmacies from "@/pages/AdminPharmacies";
import AdminApprovals from "@/pages/AdminApprovals";
import AdminPartners from "@/pages/AdminPartners";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import PatientDashboard from "@/pages/PatientDashboard";
import DoctorDashboard from "@/pages/DoctorDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import BookOPD from "@/pages/BookOPD";
import LabTests from "@/pages/LabTests";
import Pharmacy from "@/pages/Pharmacy";
import EmergencyPage from "@/pages/Emergency";
import Search from "@/pages/Search";
import VideoRoom from "@/pages/VideoRoom";
import Prescription from "@/pages/Prescription";
import Reports from "@/pages/Reports";
import AmbulanceTracker from "@/pages/AmbulanceTracker";
import FacilityDetail from "@/pages/FacilityDetail";
import AuthCallback from "@/pages/AuthCallback";
import Layout from "@/components/Layout";

function Protected({ children, role }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="p-10 text-center">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Google OAuth return: process session_id FIRST, before any route/auth check
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
        <Routes>
          <Route path="/ai-triage" element={<Layout><Protected><AITriage /></Protected></Layout>} />
          <Route path="/" element={<Layout dark><Landing /></Layout>} />
          <Route path="/login" element={<Layout><Login /></Layout>} />
          <Route path="/register" element={<Layout><Register /></Layout>} />
          <Route path="/search" element={<Layout><Search /></Layout>} />
          <Route path="/book-opd" element={<Layout><BookOPD /></Layout>} />
          <Route path="/lab-tests" element={<Layout><LabTests /></Layout>} />
          <Route path="/pharmacy" element={<Layout><Pharmacy /></Layout>} />
          <Route path="/emergency" element={<Layout><EmergencyPage /></Layout>} />
          <Route path="/facility/:kind/:id" element={<Layout><FacilityDetail /></Layout>} />
          <Route path="/prescription" element={<Layout><Protected><Prescription /></Protected></Layout>} />
          <Route path="/reports" element={<Layout><Protected><Reports /></Protected></Layout>} />
          <Route path="/track/:id" element={<Layout><Protected><AmbulanceTracker /></Protected></Layout>} />
          <Route path="/video/:room" element={<Layout dark><Protected><VideoRoom /></Protected></Layout>} />
          <Route path="/patient" element={<Layout><Protected role="patient"><PatientDashboard /></Protected></Layout>} />
          <Route path="/doctor" element={<Layout><Protected role="doctor"><DoctorDashboard /></Protected></Layout>} />
          <Route path="/admin" element={<Layout><Protected role="admin"><AdminDashboard /></Protected></Layout>} />
          <Route path="/admin/hospitals" element={<Layout><Protected role="admin"><AdminHospitals /></Protected></Layout>} />
          <Route path="/admin/labs" element={<Layout><Protected role="admin"><AdminLabs /></Protected></Layout>} />
          <Route path="/admin/pharmacies" element={<Layout><Protected role="admin"><AdminPharmacies /></Protected></Layout>} />
          <Route path="/admin/approvals" element={<Layout><Protected role="admin"><AdminApprovals /></Protected></Layout>} />
          <Route path="/admin/partners" element={<Layout><Protected role="admin"><AdminPartners /></Protected></Layout>} />
          <Route path="/partner" element={<Layout><Protected role="partner"><PartnerPortal /></Protected></Layout>} />
          <Route path="/driver" element={<Layout><Protected role="driver"><DriverApp /></Protected></Layout>} />
          <Route path="/field" element={<Layout><Protected><FieldExec /></Protected></Layout>} />
        </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <LocationProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      </LocationProvider>
    </AuthProvider>
  );
}

export default App;
